import { useAppSelector } from "redux/hooks";

/**
 * Theme-aware colours for recharts.
 *
 * Charts are rendered as inline-styled SVG, so the SCSS dark-mode rules and the
 * Siemens iX tokens in scss/global/_ix-dark-tokens.scss cannot reach them. This
 * hook mirrors the parts of that palette the charts need, picked from the same
 * source: the iX brand-dark token set for dark mode, and the existing
 * light-theme colours (unchanged) for classic mode.
 *
 * Keep the dark values in step with _ix-dark-tokens.scss if that file changes.
 */

// Categorical series palette. Light keeps the original colours so the classic
// theme is untouched; dark is the same hues lifted for legibility on #000028.
const SERIES_LIGHT = [
    "#009999", "#56C692", "#F4D03F", "#0090B2", "#6A6DB6", "#985397",
    "#AC3D67", "#FF8749", "#95B1B0", "#B47F2A", "#9FAF0E",
];

const SERIES_DARK = [
    "#00cccc", "#4dd384", "#ffd732", "#3aa8cc", "#8f92d6", "#c77ac4",
    "#e0718f", "#ff9f66", "#b3b3be", "#d9a54d", "#c3d43a",
];

export interface ChartTheme {
    isDark: boolean;
    /** Categorical palette for pie cells / multi-series charts. */
    series: string[];
    /** Text drawn on top of a bar or pie slice (LabelList). */
    labelOnFill: string;
    /** Text drawn on the chart background (axis ticks, legend). */
    labelOnBackground: string;
    /** Axis lines, ticks and grid lines. */
    axis: string;
    grid: string;
    /** Named series colours used by the dashboard charts. */
    active: string;
    closed: string;
    completed: string;
    inactive: string;
    sapContracts: string;
    inspectionContracts: string;
    passed: string;
    failed: string;
    untested: string;
}

const LIGHT: ChartTheme = {
    isDark: false,
    series: SERIES_LIGHT,
    labelOnFill: "black",
    labelOnBackground: "#37465a",
    axis: "#666666",
    grid: "#e0e0e0",
    active: "#1981BE",
    closed: "#95B1B0",
    completed: "#009999",
    inactive: "#FF856C",
    sapContracts: "#009999",
    inspectionContracts: "#7886C2",
    passed: "#27B099",
    failed: "#bd6482",
    untested: "#95B1B0",
};

const DARK: ChartTheme = {
    isDark: true,
    series: SERIES_DARK,
    // iX brand-dark: a filled bar/slice takes dark text (primary--contrast).
    labelOnFill: "#000028",
    // ...but text on the chart background takes the standard light text.
    labelOnBackground: "#ffffff",
    axis: "#7d8099",   // --theme-color-7
    grid: "#333353",   // --theme-color-1--hover
    active: "#3aa8cc",
    closed: "#b3b3be",
    completed: "#00cccc",
    inactive: "#ff9f66",
    sapContracts: "#00cccc",
    inspectionContracts: "#8f92d6",
    passed: "#28bf66",
    failed: "#ff8e9c",
    untested: "#b3b3be",
};

/**
 * `mode === 1` is dark. App.tsx and Layout.tsx run two effects that both set
 * the body class: one on `themeMode`, one on the logged-in user's saved
 * default. Whichever ran last wins, so read the same two values here.
 *
 * `themeMode` starts at 0 rather than undefined, so it cannot be used to detect
 * "not yet chosen" - fall back to the saved default only while it is still 0.
 */
export const useChartTheme = (): ChartTheme => {
    const themeMode = useAppSelector((state) => state.personalSettings.themeMode);
    const savedMode = useAppSelector(
        (state) => state.administration.loggedInUserDetails?.userDefaults?.mode
    );

    const isDark = themeMode === 1 || (themeMode === 0 && savedMode === 1);

    return isDark ? DARK : LIGHT;
};

export default useChartTheme;
